import { passwordProblem, type PasswordProblem } from '@antoja/shared';

/** Errors the account screen can show (rules from @antoja/shared, answers from Supabase). */
export type PasswordError =
  | PasswordProblem
  | 'mismatch'
  | 'weak_password'
  | 'same_password'
  | 'reauthentication_needed'
  | 'rate_limited'
  | 'failed';

export function checkPassword(
  password: string,
  confirmation: string,
  email?: string,
): PasswordError | null {
  return passwordProblem(password, email) ?? (password === confirmation ? null : 'mismatch');
}

/** One line of the live checklist: nothing typed yet, met, or not met. */
export type CheckState = 'pending' | 'ok' | 'problem';

export interface PasswordChecks {
  /** At least 12 characters (and not over the 72-byte limit). */
  readonly length: CheckState;
  readonly tooLong: boolean;
  /** Not guessable. Judged once the length is right, as the shared rule does. */
  readonly guessable: CheckState;
  readonly match: CheckState;
}

/** The same rules as checkPassword, one by one, for the checklist shown while typing. */
export function passwordChecks(
  password: string,
  confirmation: string,
  email?: string,
): PasswordChecks {
  const problem = passwordProblem(password, email);
  const typed = password.length > 0;
  return {
    length: !typed
      ? 'pending'
      : problem === 'too_short' || problem === 'too_long'
        ? 'problem'
        : 'ok',
    tooLong: problem === 'too_long',
    guessable:
      !typed || problem === 'too_short' || problem === 'too_long'
        ? 'pending'
        : problem === 'common'
          ? 'problem'
          : 'ok',
    match: !confirmation ? 'pending' : typed && password === confirmation ? 'ok' : 'problem',
  };
}
